import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../AppIcon';
import { TIMEZONE_OPTIONS } from '../../utils/timezoneUtils';

const INDUSTRY_OPTIONS = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Marketing',
  'E-commerce', 'Real Estate', 'Consulting', 'Manufacturing',
  'Media & Entertainment', 'Non-profit', 'Government', 'Other',
];

const COMPANY_SIZE_OPTIONS = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '1000+', label: '1000+ employees' },
];

const CULTURE_VALUES = [
  'Remote-first', 'Work-life balance', 'Innovation', 'Collaboration',
  'Diversity & Inclusion', 'Continuous learning', 'Fast-paced',
  'Customer-focused', 'Data-driven', 'Entrepreneurial', 'Flexible',
  'Results-oriented', 'Team-oriented', 'Growth-minded',
];

const BENEFITS_OPTIONS = [
  'Health insurance', 'Dental insurance', 'Vision insurance',
  'Flexible hours', 'Remote work', 'Professional development',
  'Paid time off', 'Retirement plan', 'Equipment provided',
  'Wellness programs', 'Team events', 'Performance bonuses',
  'Gym membership', 'Mental health support', 'Learning stipend',
];

const COMMUNICATION_PREFERENCES = [
  'Slack', 'Microsoft Teams', 'Email', 'Video calls', 'Asana',
  'Trello', 'Monday.com', 'Discord', 'WhatsApp', 'Zoom',
  'Google Meet', 'Notion', 'ClickUp',
];

const BUDGET_RANGES = [
  '$5-15/hour', '$15-25/hour', '$25-40/hour', '$40-60/hour',
  '$60+/hour', 'Project-based', 'Monthly retainer',
];

const LANGUAGE_OPTIONS = [
  'English', 'Spanish', 'French', 'German', 'Portuguese',
  'Mandarin', 'Japanese', 'Other',
];

const STEPS = [
  { id: 1, title: 'Company Basics', icon: 'Building2', description: 'Tell us about your company' },
  { id: 2, title: 'Location & Contact', icon: 'MapPin', description: 'How can VAs reach you?' },
  { id: 3, title: 'About Your Company', icon: 'FileText', description: 'Share your company story' },
  { id: 4, title: 'Culture & Values', icon: 'Heart', description: 'What defines your workplace?' },
  { id: 5, title: 'Benefits & Budget', icon: 'Gift', description: 'What do you offer?' },
  { id: 6, title: 'Work Style', icon: 'Clock', description: 'How your team operates' },
  { id: 7, title: 'Branding', icon: 'Image', description: 'Your visual identity' },
];

const CheckboxGrid = ({ options, selected, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
    {options.map((option) => {
      const isChecked = selected.includes(option);
      return (
        <div
          key={option}
          role="checkbox"
          aria-checked={isChecked}
          tabIndex={0}
          onClick={() => onChange(option)}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange(option); } }}
          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
            isChecked
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-border hover:border-primary/40 hover:bg-surface-alt'
          }`}
        >
          <span
            className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
              isChecked ? 'bg-primary border-primary' : 'border-border'
            }`}
          >
            {isChecked && (
              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="text-sm font-medium">{option}</span>
        </div>
      );
    })}
  </div>
);

const CompanyProfileWizard = ({ onSuccess }) => {
  const { userProfile, updateProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    industry: '',
    company_size: '',
    founded_year: '',
    location: '',
    phone: '',
    website: '',
    linkedin: '',
    bio: '',
    company_culture: [],
    hiring_budget_range: '',
    benefits: [],
    timezone: '',
    preferred_communication: [],
    languages_spoken: ['English'],
    company_logo: '',
    company_banner: '',
    certifications: [],
  });

  useEffect(() => {
    if (userProfile) {
      setFormData({
        company_name: userProfile.company_name || '',
        industry: userProfile.industry || '',
        company_size: userProfile.company_size || '',
        founded_year: userProfile.founded_year || '',
        location: userProfile.location || '',
        phone: userProfile.phone || '',
        website: userProfile.website || '',
        linkedin: userProfile.linkedin || '',
        bio: userProfile.bio || '',
        company_culture: (() => {
          const v = userProfile.company_culture;
          if (Array.isArray(v)) return v;
          if (typeof v === 'string' && v.startsWith('[')) {
            try { return JSON.parse(v); } catch { return []; }
          }
          return v ? [v] : [];
        })(),
        hiring_budget_range: userProfile.hiring_budget_range || '',
        benefits: Array.isArray(userProfile.benefits)
          ? userProfile.benefits
          : userProfile.benefits ? [userProfile.benefits] : [],
        timezone: userProfile.timezone || '',
        preferred_communication: Array.isArray(userProfile.preferred_communication)
          ? userProfile.preferred_communication
          : [],
        languages_spoken: Array.isArray(userProfile.languages_spoken)
          ? userProfile.languages_spoken
          : ['English'],
        company_logo: userProfile.company_logo || '',
        company_banner: userProfile.company_banner || '',
        certifications: Array.isArray(userProfile.certifications)
          ? userProfile.certifications
          : [],
      });
    }
  }, [userProfile]);

  const set = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const toggle = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));

  const validateStep = () => {
    if (currentStep === 1 && !formData.company_name.trim()) {
      setStepError('Company name is required to continue.');
      return false;
    }
    setStepError('');
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  };

  const goBack = () => {
    setStepError('');
    setCurrentStep((s) => Math.max(s - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        // company_culture is a text column in DB — serialize array as JSON string
        company_culture: JSON.stringify(Array.isArray(formData.company_culture) ? formData.company_culture : []),
        benefits: Array.isArray(formData.benefits) ? formData.benefits : [],
        preferred_communication: Array.isArray(formData.preferred_communication) ? formData.preferred_communication : [],
        certifications: Array.isArray(formData.certifications) ? formData.certifications : [],
        languages_spoken: Array.isArray(formData.languages_spoken) ? formData.languages_spoken : ['English'],
      };
      const result = await updateProfile(payload);
      if (!result?.success) {
        setStepError(result?.error || 'Failed to save profile.');
        return;
      }
      onSuccess?.();
    } catch (err) {
      console.error('CompanyProfileWizard submit error:', err);
      setStepError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = ((currentStep - 1) / (STEPS.length - 1)) * 100;

  // ─── Step Content ────────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">
          Company Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.company_name}
          onChange={(e) => set('company_name', e.target.value)}
          placeholder="Acme Corporation"
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">Industry</label>
          <select
            value={formData.industry}
            onChange={(e) => set('industry', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
          >
            <option value="">Select industry</option>
            {INDUSTRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">Company Size</label>
          <select
            value={formData.company_size}
            onChange={(e) => set('company_size', e.target.value)}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
          >
            <option value="">Select size</option>
            {COMPANY_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">Founded Year</label>
          <input
            type="number"
            min="1800"
            max={new Date().getFullYear()}
            value={formData.founded_year}
            onChange={(e) => set('founded_year', e.target.value)}
            placeholder={new Date().getFullYear().toString()}
            className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-text-primary mb-1.5">Location</label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => set('location', e.target.value)}
          placeholder="New York, USA"
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">Phone</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => set('phone', e.target.value)}
          placeholder="+1 (555) 000-0000"
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">Website</label>
        <input
          type="url"
          value={formData.website}
          onChange={(e) => set('website', e.target.value)}
          placeholder="https://yourcompany.com"
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        />
      </div>

      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-text-primary mb-1.5">LinkedIn</label>
        <input
          type="url"
          value={formData.linkedin}
          onChange={(e) => set('linkedin', e.target.value)}
          placeholder="https://linkedin.com/company/yourcompany"
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Describe your company's mission, what you do, and what makes you a great place to work. This is the first thing VAs read on your profile.
      </p>
      <textarea
        value={formData.bio}
        onChange={(e) => set('bio', e.target.value)}
        rows={8}
        placeholder="We are a fast-growing technology company focused on... Our team values... We're looking for VAs who..."
        className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition resize-none"
      />
      <p className="text-xs text-text-tertiary text-right">{formData.bio.length} characters</p>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">
        Select the values that best represent your company culture. This helps VAs understand your work environment before applying.
      </p>
      <CheckboxGrid
        options={CULTURE_VALUES}
        selected={formData.company_culture}
        onChange={(v) => toggle('company_culture', v)}
      />
      {formData.company_culture.length > 0 && (
        <p className="text-xs text-primary font-medium">
          {formData.company_culture.length} value{formData.company_culture.length !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">Typical Hiring Budget Range</label>
        <select
          value={formData.hiring_budget_range}
          onChange={(e) => set('hiring_budget_range', e.target.value)}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        >
          <option value="">Select a range</option>
          {BUDGET_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">Benefits Offered</h4>
        <p className="text-sm text-text-secondary mb-3">
          Select all benefits you typically offer. This helps attract the right candidates.
        </p>
        <CheckboxGrid
          options={BENEFITS_OPTIONS}
          selected={formData.benefits}
          onChange={(v) => toggle('benefits', v)}
        />
        {formData.benefits.length > 0 && (
          <p className="text-xs text-primary font-medium mt-2">
            {formData.benefits.length} benefit{formData.benefits.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">Primary Time Zone</label>
        <select
          value={formData.timezone}
          onChange={(e) => set('timezone', e.target.value)}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        >
          <option value="">Select time zone</option>
          {TIMEZONE_OPTIONS.map((tz) => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
        <p className="text-xs text-text-tertiary mt-1">Used for interview scheduling and communication</p>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">Preferred Communication Tools</h4>
        <p className="text-sm text-text-secondary mb-3">Which tools does your team use day-to-day?</p>
        <CheckboxGrid
          options={COMMUNICATION_PREFERENCES}
          selected={formData.preferred_communication}
          onChange={(v) => toggle('preferred_communication', v)}
        />
      </div>

      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">Languages Spoken</h4>
        <CheckboxGrid
          options={LANGUAGE_OPTIONS}
          selected={formData.languages_spoken}
          onChange={(v) => toggle('languages_spoken', v)}
        />
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">Company Logo URL</label>
        <input
          type="url"
          value={formData.company_logo}
          onChange={(e) => set('company_logo', e.target.value)}
          placeholder="https://example.com/logo.png"
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        />
        <p className="text-xs text-text-tertiary mt-1">Recommended: square image, 200×200 px minimum</p>
        {formData.company_logo && (
          <img
            src={formData.company_logo}
            alt="Logo preview"
            className="mt-3 w-16 h-16 object-cover rounded-lg border border-border"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">Company Banner URL</label>
        <input
          type="url"
          value={formData.company_banner}
          onChange={(e) => set('company_banner', e.target.value)}
          placeholder="https://example.com/banner.png"
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition"
        />
        <p className="text-xs text-text-tertiary mt-1">Recommended: 1200×400 px, showcases your company culture</p>
        {formData.company_banner && (
          <img
            src={formData.company_banner}
            alt="Banner preview"
            className="mt-3 w-full h-24 object-cover rounded-lg border border-border"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">Certifications & Credentials</label>
        <p className="text-xs text-text-tertiary mb-2">Enter each certification on a new line</p>
        <textarea
          value={Array.isArray(formData.certifications) ? formData.certifications.join('\n') : ''}
          onChange={(e) =>
            set('certifications', e.target.value.split('\n').filter((c) => c.trim()))
          }
          rows={4}
          placeholder={`ISO 9001:2015\nSOC 2 Compliance\nGoogle Partner`}
          className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition resize-none font-mono text-sm"
        />
      </div>
    </div>
  );

  const STEP_RENDERERS = [
    renderStep1, renderStep2, renderStep3, renderStep4,
    renderStep5, renderStep6, renderStep7,
  ];

  const stepData = STEPS[currentStep - 1];

  return (
    <div className="mx-auto">
      <div className="bg-surface rounded-2xl shadow-sm border border-border overflow-hidden">

        {/* ── Progress Header ── */}
        <div className="px-6 pt-6 pb-0">
          {/* Step counter */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              Step {currentStep} of {STEPS.length}
            </span>
            <span className="text-xs text-text-tertiary">
              {Math.round(progressPercent)}% complete
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1.5 bg-border rounded-full mb-5">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500 ease-in-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Step pills */}
          <div className="flex gap-1.5 overflow-x-auto pb-4 scrollbar-hide">
            {STEPS.map((step) => {
              const isDone = step.id < currentStep;
              const isActive = step.id === currentStep;
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-all ${
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : isDone
                        ? 'bg-green-100 text-green-700'
                        : 'bg-white/60 text-text-tertiary'
                  }`}
                >
                  {isDone ? (
                    <Icon name="Check" size={11} />
                  ) : (
                    <span className={`w-6 h-6 rounded-full text-sm flex items-center justify-center font-bold ${
                      isActive ? 'bg-white/25 text-white' : 'bg-border text-text-tertiary'
                    }`}>
                      {step.id}
                    </span>
                  )}
                  <span className="hidden text-sm sm:inline">{step.title}</span>
                  <span className="sm:hidden">{step.id}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Step Body ── */}
        <div className="px-6 py-6">
          {/* Step header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name={stepData.icon} size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary leading-tight">{stepData.title}</h2>
              <p className="text-sm text-text-secondary">{stepData.description}</p>
            </div>
          </div>

          {/* Error banner */}
          {stepError && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
              <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
              <span>{stepError}</span>
            </div>
          )}

          {/* Dynamic step content */}
          <div key={currentStep} className="animate-in fade-in slide-in-from-right-4 duration-300">
            {STEP_RENDERERS[currentStep - 1]()}
          </div>
        </div>

        {/* ── Navigation Footer ── */}
        <div className="px-6 py-4 bg-surface-alt border-t border-border flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-text-secondary border border-border hover:bg-surface hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            <Icon name="ChevronLeft" size={16} />
            Back
          </button>

          <div className="flex items-center gap-2">
            {/* Dot indicators */}
            {STEPS.map((step) => (
              <span
                key={step.id}
                className={`rounded-full transition-all duration-300 ${
                  step.id === currentStep
                    ? 'w-4 h-2 bg-primary'
                    : step.id < currentStep
                      ? 'w-2 h-2 bg-green-400'
                      : 'w-2 h-2 bg-border'
                }`}
              />
            ))}
          </div>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={goNext}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition shadow-sm"
            >
              Next
              <Icon name="ChevronRight" size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
            >
              {loading && <Icon name="Loader2" size={16} className="animate-spin" />}
              {loading ? 'Saving…' : 'Complete Setup'}
              {!loading && <Icon name="Check" size={16} />}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default CompanyProfileWizard;
